import React, { useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

// Autocomplete search over existing deals. Suggests full "address, city" strings
// from deals already in the system and auto-populates the field on select.
export default function DealSearch({ deals = [], value, onChange, placeholder = "Search address, city or state…", testId = "deal-search", style }) {
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return [];
    const seen = new Set();
    const out = [];
    for (const d of deals) {
      const full = `${d.address || ""}${d.city ? `, ${d.city}` : ""}`.trim();
      const key = full.toLowerCase();
      if (!key || seen.has(key)) continue;
      if (key.includes(q)) { seen.add(key); out.push({ id: d.id, full, city: d.city }); }
      if (out.length >= 6) break;
    }
    return out;
  }, [deals, value]);

  const select = (full) => {
    onChange(full);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", ...style }}>
      <div style={{ position: "relative" }}>
        <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted-2)", pointerEvents: "none" }} />
        <input
          className="field"
          data-testid={testId}
          value={value}
          placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
          style={{ paddingLeft: 36, paddingRight: value ? 34 : 12 }}
        />
        {value && (
          <button
            data-testid={`${testId}-clear`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onChange(""); setOpen(false); }}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--muted-2)", cursor: "pointer", display: "flex" }}
          ><X size={15} /></button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          onMouseEnter={() => blurTimer.current && clearTimeout(blurTimer.current)}
          style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, background: "#17161c", border: "1px solid rgba(201,162,75,0.25)", borderRadius: 4, overflow: "hidden", boxShadow: "0 12px 30px rgba(0,0,0,0.5)" }}
        >
          {suggestions.map((s) => (
            <button
              key={s.id}
              data-testid={`${testId}-option`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(s.full)}
              style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "0.7rem 0.9rem", cursor: "pointer", color: "var(--text)", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 8 }}
              onMouseOver={(e) => (e.currentTarget.style.background = "rgba(201,162,75,0.08)")}
              onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <Search size={12} style={{ color: "var(--gold)", flexShrink: 0 }} /> {s.full}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
